import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ChevronRight, Loader, CheckCircle, FileText, Check, ArrowLeft } from 'lucide-react';
import Swal from 'sweetalert2';
import { fetchSurveyBySpecialty, getFreshIdToken } from '../services/FirebaseService';
import { submitSurveyAnswer, fetchSurveyAnswers } from '../services/consultationService';
import { mapSavedAnswersToState } from '../services/surveyAnswers';

/**
 * SurveyFlow — Full-screen survey component for medical history questionnaires.
 *
 * Supports question_type:
 *   - simple_choice   → radio-style (auto-advances when no triggering sub-questions)
 *   - multiple_choice  → checkbox multi-select
 *   - char_box         → single-line text input
 *   - text_box         → multi-line textarea
 *   - date             → date picker
 */
const SurveyFlow = ({ specialtyId, surveyId, authToken, userId, onComplete, onBack }) => {
  const [subStep, setSubStep] = useState('splash'); // splash | questions | submitting | done
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const autoAdvanceTimer = useRef(null);
  // Token fresco para todas las llamadas de survey (los ID tokens expiran a la hora).
  const tokenRef = useRef(authToken);
  // El `surveyId` que llega por props NO es confiable: viene de
  // checkUserSurveyCompleted, que devuelve `data` = id de la PREGUNTA pendiente,
  // no del survey. El id real del survey es el del doc de Firestore (`survey.id`).
  const surveyIdRef = useRef(surveyId);

  // ─── Load survey data from Firebase + prefill de respuestas previas ───
  useEffect(() => {
    const loadSurvey = async () => {
      setLoading(true);
      try {
        const survey = await fetchSurveyBySpecialty(specialtyId);
        if (survey) {
          const nonConditional = (survey.questions || []).filter(q => !q.is_conditional);
          if (nonConditional.length === 0) {
            // No questions = survey already completed or empty → skip directly
            console.log('[SurveyFlow] 0 questions loaded — auto-completing');
            if (onComplete) onComplete();
            return;
          }
          setQuestions(nonConditional);

          // Id real del survey (Firestore mirror de get_model_data → `id`).
          const realSurveyId = survey.id || surveyId;
          surveyIdRef.current = realSurveyId;

          // Prefill: trae respuestas ya guardadas para no obligar a repetirlas.
          try {
            const freshToken = (await getFreshIdToken()) || authToken;
            tokenRef.current = freshToken;
            if (realSurveyId && userId) {
              const prev = await fetchSurveyAnswers(freshToken, userId, realSurveyId);
              if (prev.success && prev.answers && Object.keys(prev.answers).length > 0) {
                const prefilled = mapSavedAnswersToState(prev.answers, survey.questions || []);
                if (Object.keys(prefilled).length > 0) setAnswers(prefilled);
              }
            }
          } catch (prefillErr) {
            console.warn('[SurveyFlow] prefill skipped:', prefillErr);
          }
        } else {
          // No survey found for this specialty → nothing to fill, skip
          console.log('[SurveyFlow] No survey found for specialty — auto-completing');
          if (onComplete) onComplete();
          return;
        }
      } catch (err) {
        console.error('[SurveyFlow] Error loading survey:', err);
      } finally {
        setLoading(false);
      }
    };
    loadSurvey();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyId, surveyId, userId]);

  useEffect(() => () => { if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current); }, []);

  // ─── Triggering questions ───
  const getTriggeringQuestions = useCallback((parentQuestion) => {
    if (!parentQuestion?.contain_triggering_questions || !parentQuestion?.triggering_questions) return [];
    const selectedOptionId = answers[parentQuestion.id]?.selectedOptionId;
    if (!selectedOptionId) return [];
    return parentQuestion.triggering_questions.filter(tq => tq.triggering_answer_id?.id === selectedOptionId);
  }, [answers]);

  // ─── Handlers ───
  const handleOptionSelect = (questionId, option, parentQuestion) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        value_str: null, value_int: null,
        value_options: [option.id],
        selectedOptionId: option.id,
        selectedOptionIds: null,
        files: []
      }
    }));

    // Auto-advance for simple_choice on parent questions without triggering sub-questions
    if (parentQuestion && !parentQuestion.contain_triggering_questions) {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = setTimeout(() => {
        const idx = questions.findIndex(q => q.id === questionId);
        if (idx >= 0 && idx < questions.length - 1) {
          setCurrentQuestionIndex(idx + 1);
        } else if (idx === questions.length - 1) {
          handleSubmitAll();
        }
      }, 350);
    }
  };

  const handleMultiOptionToggle = (questionId, option) => {
    setAnswers(prev => {
      const existing = prev[questionId]?.selectedOptionIds || [];
      const isSelected = existing.includes(option.id);
      const newIds = isSelected ? existing.filter(id => id !== option.id) : [...existing, option.id];
      return {
        ...prev,
        [questionId]: {
          value_str: null, value_int: null,
          value_options: newIds,
          selectedOptionId: null,
          selectedOptionIds: newIds,
          files: []
        }
      };
    });
  };

  const handleTextChange = (questionId, text) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        value_str: text, value_int: null,
        value_options: [],
        selectedOptionId: null, selectedOptionIds: null,
        files: []
      }
    }));
  };

  const handleDateChange = (questionId, dateStr) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: {
        value_str: dateStr, value_int: null,
        value_options: [],
        selectedOptionId: null, selectedOptionIds: null,
        files: []
      }
    }));
  };

  // ─── Validation ───
  const isQuestionAnswered = (q) => {
    const answer = answers[q.id];
    if (!answer) return false;
    switch (q.question_type) {
      case 'simple_choice': return answer.selectedOptionId != null;
      case 'multiple_choice': return (answer.selectedOptionIds || []).length > 0;
      case 'char_box': return (answer.value_str || '').trim().length > 0;
      case 'text_box':
        if (q.validation?.validation_required) return (answer.value_str || '').trim().length > 0;
        return true;
      case 'date': return (answer.value_str || '').trim().length > 0;
      default: return true;
    }
  };

  const canProceed = () => {
    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return false;
    if (!isQuestionAnswered(currentQ)) return false;
    const trigQs = getTriggeringQuestions(currentQ);
    for (const tq of trigQs) {
      if (!isQuestionAnswered(tq)) return false;
    }
    return true;
  };

  const shouldShowNextButton = () => {
    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return true;
    if (currentQ.question_type === 'simple_choice' && !currentQ.contain_triggering_questions) return false;
    return true;
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      handleSubmitAll();
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) setCurrentQuestionIndex(prev => prev - 1);
  };

  // ─── Submit ───
  const handleSubmitAll = async () => {
    setSubStep('submitting');
    setSubmitting(true);
    try {
      // Token fresco: los ID tokens de Firebase expiran a la hora y el flujo
      // del cuestionario puede tardar más; un token rancio hace que Odoo
      // rechace el guardado en silencio.
      const freshToken = (await getFreshIdToken()) || tokenRef.current || authToken;
      tokenRef.current = freshToken;

      const answeredIds = Object.keys(answers).map(Number);
      const failed = [];
      for (const qId of answeredIds) {
        const ans = answers[qId];
        if (!ans.value_str && !ans.value_int && (!ans.value_options || ans.value_options.length === 0)) continue;
        const res = await submitSurveyAnswer(freshToken, userId, surveyIdRef.current, qId, {
          value_int: ans.value_int,
          value_str: ans.value_str,
          value_options: ans.value_options || [],
          files: ans.files || []
        });
        // No marcar "guardado" si el backend rechazó: así no se pierde el
        // expediente creyendo que quedó grabado.
        if (!res || res.success !== true) failed.push(qId);
      }

      if (failed.length > 0) {
        throw new Error(`No se guardaron ${failed.length} respuesta(s)`);
      }

      setSubStep('done');
      setTimeout(() => { if (onComplete) onComplete(); }, 1200);
    } catch (err) {
      console.error('[SurveyFlow] submission error:', err);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Hubo un problema al guardar tus respuestas. Tus respuestas siguen aquí; intenta de nuevo.', confirmButtonColor: '#786ED9' });
      setSubStep('questions');
    } finally {
      setSubmitting(false);
    }
  };

  // ═══════════════════════════════════════════
  // Render helpers — question types
  // ═══════════════════════════════════════════

  const renderSimpleChoice = (q, isTriggering = false) => {
    const opts = q.options || [];
    if (opts.length === 0) return null;
    return (
      <div className="space-y-3 mb-4">
        {opts.map(opt => {
          const isSelected = answers[q.id]?.selectedOptionId === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => isTriggering ? handleOptionSelect(q.id, opt, null) : handleOptionSelect(q.id, opt, q)}
              className={`press-scale-card w-full text-left ${isTriggering ? "p-3" : "p-4"} rounded-input transition-all font-medium flex items-center gap-3 ${
                isSelected
                  ? 'bg-daisies-50 text-daisies ring-2 ring-brand'
                  : 'bg-white text-text-primary hover:bg-daisies-50'
              }`}
            >
              <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                isSelected ? "bg-daisies" : "bg-brand-tint"
              }`}>
                {isSelected && <span className="w-2 h-2 rounded-full bg-white" />}
              </span>
              <span className="text-sm">{opt.value}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderMultipleChoice = (q, isTriggering = false) => {
    const opts = q.options || [];
    if (opts.length === 0) return null;
    const selectedIds = answers[q.id]?.selectedOptionIds || [];
    return (
      <div className="space-y-3 mb-4">
        {opts.map(opt => {
          const isSelected = selectedIds.includes(opt.id);
          return (
            <button
              key={opt.id}
              onClick={() => handleMultiOptionToggle(q.id, opt)}
              className={`press-scale-card w-full text-left ${isTriggering ? "p-3" : "p-4"} rounded-input transition-all font-medium flex items-center gap-3 ${
                isSelected
                  ? 'bg-daisies-50 text-daisies ring-2 ring-brand'
                  : 'bg-white text-text-primary hover:bg-daisies-50'
              }`}
            >
              <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 transition-colors ${
                isSelected ? "bg-daisies" : "bg-brand-tint"
              }`}>
                {isSelected && <Check size={12} className="text-white" />}
              </span>
              <span className="text-sm">{opt.value}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderCharBox = (q, isTriggering = false) => (
    <input
      type="text"
      value={answers[q.id]?.value_str || ''}
      onChange={e => handleTextChange(q.id, e.target.value)}
      placeholder="Escribe tu respuesta..."
      autoFocus
      className={`w-full ${isTriggering ? 'p-3 text-sm' : 'p-4'} rounded-input bg-warm-canvas focus:ring-2 focus:ring-brand focus:outline-none text-text-primary transition-all mb-4`}
    />
  );

  const renderTextBox = (q, isTriggering = false) => (
    <textarea
      value={answers[q.id]?.value_str || ''}
      onChange={e => handleTextChange(q.id, e.target.value)}
      placeholder="Escribe tu respuesta..."
      rows={3}
      autoFocus
      className={`w-full ${isTriggering ? 'p-3 text-sm' : 'p-4'} rounded-input bg-warm-canvas focus:ring-2 focus:ring-brand focus:outline-none text-text-primary resize-none transition-all mb-4`}
    />
  );

  const renderDateInput = (q, isTriggering = false) => (
    <input
      type="date"
      value={answers[q.id]?.value_str || ''}
      onChange={e => handleDateChange(q.id, e.target.value)}
      className={`w-full ${isTriggering ? 'p-3 text-sm' : 'p-4'} rounded-input bg-warm-canvas focus:ring-2 focus:ring-brand focus:outline-none text-text-primary transition-all mb-4`}
    />
  );

  const renderQuestion = (q, isTriggering = false) => {
    switch (q.question_type) {
      case 'simple_choice': return renderSimpleChoice(q, isTriggering);
      case 'multiple_choice': return renderMultipleChoice(q, isTriggering);
      case 'char_box': return renderCharBox(q, isTriggering);
      case 'text_box': return renderTextBox(q, isTriggering);
      case 'date': return renderDateInput(q, isTriggering);
      default: return renderCharBox(q, isTriggering);
    }
  };

  // ═══════════════════════════════════════════
  // ─── Splash Screen ───
  // ═══════════════════════════════════════════
  if (subStep === 'splash') {
    return (
      <div className="max-w-lg mx-auto px-4 py-8 min-h-screen flex flex-col items-center justify-center">
        <div className="animate-fadeInUp text-center">
          <div className="mx-auto w-20 h-20 rounded-full bg-daisies flex items-center justify-center mb-6">
            <FileText size={36} className="text-white" />
          </div>

          <h2 className="text-2xl font-bold text-text-primary mb-3 tracking-tight">
            Expediente médico
          </h2>
          <p className="text-text-secondary text-base leading-relaxed mb-8">
            Antes de comenzar con tu consulta tenemos que hacerte unas preguntas para conocer tus antecedentes médicos, te tomará un minuto.
          </p>

          <button
            onClick={() => setSubStep('questions')}
            disabled={loading}
            className="press-scale w-full py-4 bg-daisies text-white rounded-full text-lg font-semibold hover:bg-daisies-hover transition-all"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader size={20} className="animate-spin" /> Cargando...
              </span>
            ) : (
              'Comenzar'
            )}
          </button>

          <button
            onClick={onBack}
            className="press-scale mt-4 inline-flex items-center gap-1.5 text-sm text-text-mute hover:text-brand transition-colors font-medium"
          >
            <ArrowLeft size={16} aria-hidden="true" /> Volver
          </button>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // ─── Submitting / Done ───
  // ═══════════════════════════════════════════
  if (subStep === 'submitting' || subStep === 'done') {
    return (
      <div className="max-w-lg mx-auto px-4 py-8 min-h-screen flex flex-col items-center justify-center">
        <div className="animate-fadeInUp text-center">
          {subStep === 'submitting' ? (
            <>
              <Loader size={48} className="animate-spin text-daisies mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-text-primary">Guardando tus respuestas...</h3>
              <p className="text-text-secondary mt-2">Esto tomará un momento</p>
            </>
          ) : (
            <>
              <div className="mx-auto w-16 h-16 rounded-full bg-mint-pill flex items-center justify-center mb-4">
                <CheckCircle size={36} className="text-mint-deep" />
              </div>
              <h3 className="text-xl font-semibold text-text-primary">¡Listo!</h3>
              <p className="text-text-secondary mt-2">Tu expediente médico ha sido completado</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════
  // ─── Questions ───
  // ═══════════════════════════════════════════
  const currentQ = questions[currentQuestionIndex];
  const triggeringQs = currentQ ? getTriggeringQuestions(currentQ) : [];
  const totalNonConditional = questions.length;
  const progress = ((currentQuestionIndex + 1) / totalNonConditional) * 100;
  const showNext = shouldShowNextButton();

  return (
    <div className="max-w-lg mx-auto px-4 py-4 md:py-8 min-h-screen flex flex-col">
      {/* Header */}
      <div className="mb-4">
        <button onClick={onBack} className="press-scale mb-4 text-daisies flex items-center gap-2 hover:gap-3 transition-all text-sm font-medium">
          <ArrowLeft size={18} /> Volver
        </button>

        {/* Progress bar */}
        <div className="w-full h-2 bg-surface rounded-full overflow-hidden mb-2">
          <div
            className="h-full rounded-full transition-all duration-500 bg-daisies"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="ds-caption text-text-secondary text-right">
          {currentQuestionIndex + 1} de {totalNonConditional}
        </p>
      </div>

      {/* Question card */}
      <div className="flex-1 flex flex-col">
        {currentQ && (
          <div className="bg-white rounded-card p-5 md:p-6 mb-4 flex-1 flex flex-col animate-fadeIn">
            {/* Section name */}
            {currentQ.section?.name && (
              <p className="ds-caption text-daisies uppercase mb-1">
                {currentQ.section.name}
              </p>
            )}

            {/* Subtitle */}
            {currentQ.subtitle && currentQ.subtitle !== '.' && currentQ.subtitle.trim() !== '' && (
              <p className="text-sm text-text-secondary mb-1">{currentQ.subtitle}</p>
            )}

            <h3 className="text-lg font-bold text-text-primary mb-6 leading-snug tracking-tight">
              {currentQ.title}
            </h3>

            {/* Render question */}
            {renderQuestion(currentQ, false)}

            {/* Triggering (conditional) questions */}
            {triggeringQs.map(tq => (
              <div key={tq.id} className="mt-4 pl-4 border-l-2 border-daisies/30 animate-slideDown">
                <h4 className="text-base font-semibold text-text-primary mb-3">{tq.title}</h4>
                {renderQuestion(tq, true)}
              </div>
            ))}
          </div>
        )}

        {/* Navigation */}
        <div className="flex gap-3 mt-auto pb-2">
          {currentQuestionIndex > 0 && (
            <button
              onClick={handlePrev}
              className="press-scale flex-1 py-4 border-[1.5px] border-brand text-daisies rounded-full font-semibold text-base transition-all hover:bg-daisies/5"
            >
              Anterior
            </button>
          )}
          {showNext && (
            <button
              onClick={handleNext}
              disabled={!canProceed() || submitting}
              className={`press-scale flex-1 py-4 rounded-full text-white font-semibold text-base transition-all ${
                canProceed() && !submitting
                  ? 'bg-daisies hover:bg-daisies-hover'
                  : 'bg-daisies/40 cursor-not-allowed'
              }`}
            >
              {currentQuestionIndex === questions.length - 1 ? (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle size={18} /> Finalizar
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Siguiente <ChevronRight size={18} />
                </span>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyFlow;
